import maplibregl from "maplibre-gl";
import * as turf from "@turf/turf";
import type { Dispatch } from "redux";
import { addAOIFeature, setDrawingAOIMode, updateAOIFeature } from "../../../store/slices/aoiSlice";
import
{
  AOI_DRAWING_FEATURE_COLOR,
  AOI_DRAWING_FEATURE_STROKE_COLOR,
  AOI_DRAWING_FEATURE_STROKE_WIDTH,
  AOI_FEATURE_COLOR,
  AOI_FEATURE_KEY,
  AOI_LAYER_ID,
  AOI_MODIFY_FEATURE_COLOR,
  AOI_MODIFY_FEATURE_STROKE_COLOR,
  AOI_MODIFY_FEATURE_STROKE_WIDTH,
  AOI_ACTIVE_FEATURE_COLOR,
  AOI_SOURCE_ID,
  EDIT_AOI_SOURCE_ID,
  AOI_ACTIVE_FEATURE_STROKE_COLOR,
  AOI_ACTIVE_FEATURE_STROKE_WIDTH,
  AOI_INACTIVE_LINE_DASH,
  AOI_INVALID_FEATURE_COLOR,
  AOI_INVALID_FEATURE_STROKE_COLOR,
} from "../consts";
import type { AOICoordinate, DrawAOIFeature, DrawAOIGeometry, DrawingAOIMode } from "../types";
import type { UnknownAction } from "redux";

// ── Types ─────────────────────────────────────────────────────────────────────

type MLMap = {
  on: (ev: string, h: (e: unknown) => void) => void;
  off: (ev: string, h: (e: unknown) => void) => void;
  remove: () => void;
  addSource: (id: string, spec: object) => void;
  removeSource: (id: string) => void;
  getSource: (id: string) => undefined | { setData(d: object): void };
  addLayer: (spec: object, before?: string) => void;
  removeLayer: (id: string) => void;
  getLayer: (id: string) => unknown;
  setFilter: (id: string, filter?: unknown) => void;
  setLayoutProperty: (id: string, k: string, v: unknown) => void;
  setPaintProperty: (id: string, k: string, v: unknown) => void;
  getCenter: () => { lat: number; lng: number };
  getZoom: () => number;
  getBearing: () => number;
  getPitch: () => number;
  dragPan: { enable(): void; disable(): void };
  queryRenderedFeatures: (pt: unknown, opts: { layers: string[] }) => { properties: Record<string, unknown> }[];
  doubleClickZoom: { enable(): void; disable(): void };
  getCanvas: () => { style: CSSStyleDeclaration };
  isStyleLoaded: () => boolean;
  once: (type: string, listener?: (ev: unknown) => void) => Promise<unknown>;
};

/**
 * Area rule applied to the shape being drawn, so the limit is felt WHILE
 * dragging rather than only once the polygon is finished. The tool has no i18n
 * of its own, so the caller supplies the wording.
 */
export interface AOIAreaGuard
{
  limits?: { minKm2?: number; maxKm2?: number };
  format: (areaKm2: number, status: "ok" | "too-small" | "too-large") => string;
}

type LngLatEvent = { lngLat: { lng: number; lat: number } };
type PointEvent = { point: unknown };

interface MapDrawAOIState
{
  map: MLMap | null;
  mode: DrawingAOIMode;
  dead: boolean;
  ready: boolean;
  addedIds: Set<string>;
  drawPts: [number, number][];
  mousePos: AOICoordinate | null;
  livePoints: AOICoordinate[];
  rectStart: [number, number] | null;
  rectEnd: [number, number] | null;
  moveEndH: ((e: unknown) => void) | null;
  clickH: ((e: unknown) => void) | null;
  dblH: ((e: unknown) => void) | null;
  mdH: ((e: unknown) => void) | null;
  mmH: ((e: unknown) => void) | null;
  muH: ((e: unknown) => void) | null;
  mClickH: ((e: unknown) => void) | null;
  mDblH: ((e: unknown) => void) | null;
  mMoveH: ((e: unknown) => void) | null;
  mEditClickH: ((e: unknown) => void) | null;
  mEditMoveH: ((e: unknown) => void) | null;
  mEditUpH: ((e: unknown) => void) | null;
  lastClickMs: number;
  editDrag: { segId: string; vIdx: number } | null;
  dragIdx: number;
  dragId: string | null;
  dragGeom: DrawAOIGeometry | null;
  editingFeatureId: string | null;
  activeFeatureId: string | null;
  features: DrawAOIFeature[];
  areaGuard: AOIAreaGuard | null;
  liveAreaMarker: maplibregl.Marker | null;
  // move-polygon state (NONE mode)
  moveFeatureId: string | null;
  moveDragStart: AOICoordinate | null;
  moveDragGeom: DrawAOIGeometry | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Extract [lng, lat] from a map event */
const toLngLat = (e: unknown): AOICoordinate =>
{
  const ev = e as LngLatEvent;
  return [ev.lngLat.lng, ev.lngLat.lat];
};

/** Ensure polygon ring is closed (last point === first point) */
const closePolygon = (coords: AOICoordinate[]): AOICoordinate[] =>
{
  if (coords.length < 2) return coords;
  const first = coords[0],
    last = coords[coords.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? coords : [...coords, first];
};

/** Strip closing duplicate from polygon ring */
const openPolygon = (coords: AOICoordinate[]): AOICoordinate[] =>
{
  if (coords.length < 2) return coords;
  const first = coords[0],
    last = coords[coords.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? coords.slice(0, -1) : coords;
};

/** Build feature GeoJSON list (non-preview features) */
const buildExistingFeats = (
  features: DrawAOIFeature[],
  activeFeatureId: string | null,
): object[] =>
  features
    .filter((f: any) => !f.preview)
    .map((f: any) => ({
      ...f,
      properties: {
        ...f.properties,
        color: f.fill || AOI_ACTIVE_FEATURE_COLOR,
        // Stamped here rather than at the call sites so every writer that goes
        // through this helper keeps the solid/dashed styling intact.
        active: f.id === activeFeatureId,
      },
    }));

const newFeatureId = () => `${AOI_FEATURE_KEY}-${crypto.randomUUID()}`;

/** Area of a closed lng/lat ring, km². */
const ringAreaKm2 = (ring: [number, number][]): number =>
{
  if (ring.length < 4) return 0;
  try
  {
    return turf.area(turf.polygon([ring])) / 1_000_000;
  } catch
  {
    // A self-intersecting ring mid-drag is normal; no area is better than a throw.
    return 0;
  }
};

const removeLiveArea = (S: MapDrawAOIState) =>
{
  S.liveAreaMarker?.remove();
  S.liveAreaMarker = null;
};

/**
 * Show the running area above the shape being drawn, and say whether it is
 * already out of bounds.
 *
 * Returns true when the shape currently breaks the rule, so the caller can mark
 * the preview feature and let the red paint branch take over — the polygon goes
 * red the moment the drag crosses the limit, not when the mouse is released.
 */
const syncLiveArea = (S: MapDrawAOIState, ring: [number, number][]): boolean =>
{
  const guard = S.areaGuard;
  if (!guard || !S.map)
  {
    removeLiveArea(S);
    return false;
  }

  const areaKm2 = ringAreaKm2(ring);
  if (areaKm2 <= 0)
  {
    removeLiveArea(S);
    return false;
  }

  const { minKm2, maxKm2 } = guard.limits ?? {};
  const status =
    maxKm2 !== undefined && areaKm2 > maxKm2
      ? "too-large"
      : minKm2 !== undefined && areaKm2 < minKm2
        ? "too-small"
        : "ok";

  // Anchor at the northernmost vertex, the same place the settled warning uses,
  // so the label does not jump when the drag ends.
  let top = ring[0];
  ring.forEach((point) => { if (point[1] > top[1]) top = point; });

  const invalid = status !== "ok";
  const text = guard.format(areaKm2, status);

  if (!S.liveAreaMarker)
  {
    const element = document.createElement("div");
    S.liveAreaMarker = new maplibregl.Marker({
      element,
      anchor: "bottom",
      offset: [0, -8],
      opacityWhenCovered: "0",
    })
      .setLngLat(top)
      .addTo(S.map as unknown as maplibregl.Map);
  }

  const element = S.liveAreaMarker.getElement();
  element.textContent = text;
  element.style.cssText = [
    "pointer-events:none",
    "white-space:nowrap",
    "padding:3px 7px",
    "border-radius:6px",
    "font-size:11px",
    "font-weight:600",
    "box-shadow:0 2px 10px rgba(0,0,0,0.45)",
    invalid
      ? "border:1px solid rgba(248,113,113,0.55);background:rgba(40,8,12,0.92);color:#fca5a5"
      : "border:1px solid rgba(255,255,255,0.18);background:rgba(10,14,26,0.9);color:#e2e8f0",
  ].join(";");

  S.liveAreaMarker.setLngLat(top);

  return invalid;
};

/** Ray-casting point-in-polygon (lng/lat ring, open or closed) */
const pointInPolygon = (pt: AOICoordinate, ring: AOICoordinate[]): boolean =>
{
  const [px, py] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++)
  {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/** Find the topmost AOI feature whose polygon contains a lng/lat point */
const hitTestFeatures = (pt: AOICoordinate, features: DrawAOIFeature[]): DrawAOIFeature | null =>
{
  for (let i = features.length - 1; i >= 0; i--)
  {
    const f = features[i];
    if (f.geometry.type === "Polygon" && pointInPolygon(pt, f.geometry.coordinates[0])) return f;
  }
  return null;
};

const clearState = (S: MapDrawAOIState) =>
{
  removeLiveArea(S);
  S.drawPts = [];
  S.dragIdx = -1;
  S.dragId = null;
  S.dragGeom = null;
  S.mousePos = null;
  S.livePoints = [];
  S.rectStart = null;
  S.rectEnd = null;
  S.moveFeatureId = null;
  S.moveDragStart = null;
  S.moveDragGeom = null;
};

// ── Main factory ──────────────────────────────────────────────────────────────

export function createMapLibreDrawingAOI(
  map: MLMap,
  mode: DrawingAOIMode,
  editingFeatureId: string | null,
  features: DrawAOIFeature[],
  dispatch: Dispatch<UnknownAction>,
  allowMultiple = false,
  layers?: any,
  activeFeatureId: string | null = null,
  allowMoveActive: boolean = true,
  areaGuard: AOIAreaGuard | null = null,
)
{
  const S: MapDrawAOIState = {
    map,
    mode,
    dead: false,
    ready: false,
    addedIds: new Set(),
    drawPts: [],
    mousePos: null,
    livePoints: [],
    rectStart: null,
    rectEnd: null,
    moveEndH: null,
    clickH: null,
    dblH: null,
    mdH: null,
    mmH: null,
    muH: null,
    mClickH: null,
    mDblH: null,
    mMoveH: null,
    mEditClickH: null,
    mEditMoveH: null,
    mEditUpH: null,
    lastClickMs: 0,
    editDrag: null,
    dragIdx: -1,
    dragId: null,
    dragGeom: null,
    editingFeatureId,
    activeFeatureId,
    features: features ?? [],
    moveFeatureId: null,
    moveDragStart: null,
    moveDragGeom: null,
    areaGuard,
    liveAreaMarker: null,
  };

  /** Finish drawing: dispatch, optionally reset, update map */
  const finishFeature = (feature: DrawAOIFeature, newFeatures: DrawAOIFeature[]) =>
  {
    dispatch(addAOIFeature(feature));
    if (allowMultiple)
    {
      resetTool(S);
      pushDraw(S, newFeatures);
    } else
    {
      removeTool(S);
      dispatch(setDrawingAOIMode("NONE"));
      pushDraw(S, newFeatures);
    }
  };

  return {
    activate()
    {
      initAOILayers(S);

      // ── NONE mode: drag-to-move active polygon only ────────────────────────
      if (mode === "NONE")
      {
        const mdH = (e: unknown) =>
        {
          if (!allowMoveActive) return;
          const pt = toLngLat(e);
          const feat = hitTestFeatures(pt, S.features);
          // Only react when the clicked polygon is already active
          if (!feat || feat.id !== S.activeFeatureId) return;
          if (feat.geometry.type !== "Polygon") return; // Points aren't draggable via this path

          S.moveFeatureId = feat.id;
          S.moveDragStart = pt;
          S.moveDragGeom = feat.geometry;
          map.dragPan.disable();
          map.getCanvas().style.cursor = "grabbing";
        };

        const mmH = (e: unknown) =>
        {
          // While dragging: translate the polygon (visual only, no Redux dispatch yet)
          if (S.moveFeatureId && S.moveDragStart && S.moveDragGeom && S.moveDragGeom.type === "Polygon")
          {
            const current = toLngLat(e);
            const dx = current[0] - S.moveDragStart[0];
            const dy = current[1] - S.moveDragStart[1];
            const newCoords = S.moveDragGeom.coordinates[0].map(([lng, lat]): AOICoordinate => [lng + dx, lat + dy]);
            const newGeom: DrawAOIGeometry = { type: "Polygon", coordinates: [newCoords] };
            S.features = S.features.map((f) => (f.id === S.moveFeatureId ? { ...f, geometry: newGeom } : f));
            pushDraw(S, S.features);
            S.moveDragStart = current;
            S.moveDragGeom = newGeom;
            return;
          }

          // Hover: show grab cursor over any polygon
          map.getCanvas().style.cursor = hitTestFeatures(toLngLat(e), S.features) ? "grab" : "";
        };

        const muH = () =>
        {
          // Commit final geometry to the store on drag end
          if (S.moveFeatureId && S.moveDragGeom)
          {
            dispatch(updateAOIFeature({ id: S.moveFeatureId, geometry: S.moveDragGeom }));
          }
          S.moveFeatureId = null;
          S.moveDragStart = null;
          S.moveDragGeom = null;
          map.dragPan.enable();
          map.getCanvas().style.cursor = "";
        };

        map.on("mousedown", mdH);
        map.on("mousemove", mmH);
        map.on("mouseup", muH);
        S.mdH = mdH;
        S.mmH = mmH;
        S.muH = muH;
        return;
      }

      // ── EDIT mode: drag vertices ────────────────────────────────────────────
      if (mode === "EDIT")
      {
        const mdH = (e: unknown) =>
        {
          const ev = e as PointEvent;
          const hits = map.queryRenderedFeatures(ev.point, { layers: [`${AOI_LAYER_ID}_edit-vertex`] });
          if (!hits.length) return;

          map.getCanvas().style.cursor = "move";
          S.dragIdx = Number(hits[0].properties["vertexIdx"]);
          S.dragId = String(hits[0].properties["featId"]);
          S.dragGeom = S.features.find((f) => f.id === S.dragId)?.geometry ?? null;
          map.dragPan.disable();
        };

        const mmH = (e: unknown) =>
        {
          if (S.dragIdx < 0 || !S.dragId || !S.dragGeom) return;
          if (S.dragGeom.type !== "Polygon") return; // vertex-drag editing only applies to Polygon features
          const coord = toLngLat(e);

          // Normalize polygon: remove closing duplicate before patching
          const open = openPolygon(S.dragGeom.coordinates[0]);
          const geom = { type: "Polygon" as const, coordinates: [open] };

          const ng = patchGeom(geom, S.dragIdx, coord);
          S.dragGeom = ng;

          // Re-close polygon for rendering/storage
          const storageGeom: DrawAOIGeometry = ng.type === "Polygon" ? { type: "Polygon", coordinates: [closePolygon(ng.coordinates[0])] } : ng;

          dispatch(updateAOIFeature({ id: S.dragId, geometry: storageGeom }));
          const updated = S.features.map((f) => (f.id === S.dragId ? { ...f, geometry: storageGeom } : f));
          pushDraw(S, updated);
        };

        const muH = () =>
        {
          map.getCanvas().style.cursor = "";
          S.dragIdx = -1;
          S.dragId = null;
          S.dragGeom = null;
          map.dragPan.enable();
        };

        map.on("mousedown", mdH);
        map.on("mousemove", mmH);
        map.on("mouseup", muH);
        S.mdH = mdH;
        S.mmH = mmH;
        S.muH = muH;
        return;
      }

      map.doubleClickZoom.disable();
      if (mode != "VIEW") map.getCanvas().style.cursor = "crosshair";

      // ── RECTANGLE mode: two-click corner-to-corner ─────────────────────────
      if (mode === "RECTANGLE")
      {
        const mclickH = (e: unknown) =>
        {
          const coord = toLngLat(e) as [number, number];
          S.drawPts.push(coord);
          S.livePoints = S.drawPts;

          if (!S.rectStart)
          {
            S.rectStart = coord;
            return;
          }

          S.rectEnd = coord;
          const ring = rectRing(S.rectStart, S.rectEnd);
          const coords = ring.slice(0, -1) as [number, number][];
          const properties = {
            color: AOI_ACTIVE_FEATURE_COLOR,
            fill: AOI_ACTIVE_FEATURE_COLOR,
            stroke: AOI_ACTIVE_FEATURE_STROKE_COLOR,
            strokeWidth: AOI_ACTIVE_FEATURE_STROKE_WIDTH,
          };
          const feature: DrawAOIFeature = {
            id: newFeatureId(),
            geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] },
            properties,
            ...properties,
            label: "",
            createdAt: Date.now(),
          };
          S.rectStart = null;
          S.rectEnd = null;
          map.dragPan.enable();
          finishFeature(feature, [...S.features, feature]);
        };

        const mmH = (e: unknown) =>
        {
          if (!S.rectStart || !S.livePoints?.length) return;
          const coord = toLngLat(e) as [number, number];
          S.mousePos = coord;
          S.rectEnd = coord;
          const ring = rectRing(S.rectStart, S.rectEnd);
          const coords = ring.slice(0, -1) as [number, number][];
          const closed = [...coords, coords[0]] as [number, number][];
          const invalid = syncLiveArea(S, closed);
          map.getSource(AOI_SOURCE_ID)?.setData({
            type: "FeatureCollection",
            features: [
              ...buildExistingFeats(S.features, S.activeFeatureId),
              {
                type: "Feature",
                id: newFeatureId(),
                geometry: { type: "Polygon", coordinates: [closed] },
                properties: { preview: true, invalid },
                label: "",
                createdAt: Date.now(),
              },
              { type: "Feature", geometry: { type: "Point", coordinates: S.mousePos } },
            ],
          });
        };

        map.on("click", mclickH);
        map.on("mousemove", mmH);
        S.mClickH = mclickH;
        S.mmH = mmH;
        return;
      }

      // ── POINT mode: single click drops a Point feature ─────────────────────
      if (mode === "POINT")
      {
        const clickH = (e: unknown) =>
        {
          const properties = {
            color: AOI_ACTIVE_FEATURE_COLOR,
            fill: AOI_ACTIVE_FEATURE_COLOR,
            stroke: AOI_ACTIVE_FEATURE_STROKE_COLOR,
            strokeWidth: AOI_ACTIVE_FEATURE_STROKE_WIDTH,
          };
          const feature: DrawAOIFeature = {
            id: newFeatureId(),
            geometry: { type: "Point", coordinates: toLngLat(e) },
            properties,
            ...properties,
            label: "",
            createdAt: Date.now(),
          };
          finishFeature(feature, [...S.features, feature]);
        };

        map.on("click", clickH);
        S.clickH = clickH;
        return;
      }

      // POLYGON: click to add points, dblclick to finish ───────
      const clickH = (e: unknown) =>
      {
        S.drawPts.push(toLngLat(e) as [number, number]);
        S.livePoints = S.drawPts;
        pushDraw(S, S.features);
      };

      const dblH = () =>
      {
        const pts = S.drawPts.slice(0, -1) as AOICoordinate[];
        let feature: DrawAOIFeature | null = null;

        if (pts.length >= 3)
        {
          const properties = {
            color: AOI_ACTIVE_FEATURE_COLOR,
            fill: AOI_ACTIVE_FEATURE_COLOR,
            stroke: AOI_ACTIVE_FEATURE_STROKE_COLOR,
            strokeWidth: AOI_ACTIVE_FEATURE_STROKE_WIDTH,
          };
          feature = {
            id: newFeatureId(),
            geometry: { type: "Polygon", coordinates: [[...pts, pts[0]]] },
            properties,
            ...properties,
            label: "",
            createdAt: Date.now(),
          };
        }

        if (feature) finishFeature(feature, [...S.features, feature]);
      };

      const mMoveH = (e: unknown) =>
      {
        S.mousePos = toLngLat(e);
        if (S.livePoints?.length) pushDrawLive(S);
      };

      map.on("click", clickH);
      map.on("dblclick", dblH);
      map.on("mousemove", mMoveH);
      S.clickH = clickH;
      S.dblH = dblH;
      S.mMoveH = mMoveH;
    },

    deactivate()
    {
      map.doubleClickZoom.enable();
      map.getCanvas().style.cursor = "";
      S.dead = true;
      S.ready = false;

      removeTool(S);

      try
      {
        if (S.moveEndH) map.off("moveend", S.moveEndH);
        if (S.clickH) map.off("mouseup", S.clickH);
        if (S.dblH) map.off("dblclick", S.dblH);
        if (S.mdH) map.off("mousedown", S.mdH);
        if (S.mmH) map.off("mousemove", S.mmH);
        if (S.muH) map.off("mouseup", S.muH);

        if (S.mClickH) map.off("click", S.mClickH);
        if (S.mDblH) map.off("dblclick", S.mDblH);
        if (S.mMoveH) map.off("mousemove", S.mMoveH);
        if (S.mEditClickH) map.off("click", S.mEditClickH);
        if (S.mEditMoveH) map.off("mousemove", S.mEditMoveH);
        if (S.mEditUpH) map.off("mouseup", S.mEditUpH);
        detachToolHandlers(map, S);
      } catch
      {
        /* ignore — already removed or container gone */
      }

      S.map = null;
      S.moveEndH = null;
      clearState(S);

      try
      {
        map.dragPan.enable();
      } catch
      {
        /* */
      }
    },
  };
}

// ── Layer / source initialisation ─────────────────────────────────────────────

const initAOILayers = (S: MapDrawAOIState) =>
{
  const { map, features } = S;
  if (!map) return;

  // avoid calls before style is ready
  // if (typeof map.isStyleLoaded === "function" && !map.isStyleLoaded()) {
  //   const onceKey = "__aoiLayersPending";
  //   if (!(map as any)[onceKey]) {
  //     (map as any)[onceKey] = true;
  //     map.once("styledata", () => {
  //       (map as any)[onceKey] = false;
  //       initAOILayers(S);
  //     });
  //   }
  //   return;
  // }

  try
  {
    const drawData = drawGJ(S, features);
    const editData = editGJ(S.editingFeatureId, features);

    const sourceDraw = map.getSource(AOI_SOURCE_ID);
    if (!sourceDraw) map.addSource(AOI_SOURCE_ID, { type: "geojson", data: drawData });
    else sourceDraw.setData(drawData);

    const sourceEdit = map.getSource(EDIT_AOI_SOURCE_ID);
    if (!sourceEdit) map.addSource(EDIT_AOI_SOURCE_ID, { type: "geojson", data: editData });
    else sourceEdit.setData(editData);

    const addLayerIfMissing = (spec: object) =>
    {
      const id = (spec as { id: string }).id;
      if (!map.getLayer(id))
      {
        map.addLayer(spec);
        return;
      }
      // The layer survives a basemap swap (MapLibreAdapter re-applies it via
      // transformStyle), so re-push the filter instead of skipping — otherwise a
      // hot reload leaves the old, un-narrowed filter live and every inactive AOI
      // gets stroked twice, solid over dashed.
      const filter = (spec as { filter?: unknown }).filter;
      if (filter) map.setFilter(id, filter);
    };

    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_draw-fill`,
      type: "fill",
      source: AOI_SOURCE_ID,
      paint: {
        "fill-color": [
          "case",
          ["==", ["get", "invalid"], true],
          AOI_INVALID_FEATURE_COLOR,
          ["coalesce", ["get", "color"], AOI_DRAWING_FEATURE_COLOR],
        ],
        "fill-opacity": 0.18,
      },
      filter: ["==", ["geometry-type"], "Polygon"],
    });

    // The outline is split across two layers because `line-dasharray` is a
    // cross-faded (zoom-only) property in MapLibre — putting a feature expression
    // in it fails style validation, and addLayer then bails out silently, leaving
    // the AOI with no outline at all. The two filters partition the value space:
    // `active` is only ever stamped on saved AOI features, so transient drawing
    // geometry (no such property -> null) lands in the solid layer exactly as before.
    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_draw-line`,
      type: "line",
      source: AOI_SOURCE_ID,
      paint: {
        "line-color": [
          "case",
          ["==", ["get", "invalid"], true],
          AOI_INVALID_FEATURE_STROKE_COLOR,
          ["coalesce", ["get", "stroke_color"], AOI_DRAWING_FEATURE_STROKE_COLOR],
        ],
        "line-width": AOI_DRAWING_FEATURE_STROKE_WIDTH,
      },
      filter: [
        "all",
        ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
        ["!=", ["get", "active"], false],
      ],
    });

    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_draw-line-inactive`,
      type: "line",
      source: AOI_SOURCE_ID,
      paint: {
        "line-color": [
          "case",
          ["==", ["get", "invalid"], true],
          AOI_INVALID_FEATURE_STROKE_COLOR,
          ["coalesce", ["get", "stroke_color"], AOI_DRAWING_FEATURE_STROKE_COLOR],
        ],
        "line-width": AOI_DRAWING_FEATURE_STROKE_WIDTH,
        "line-dasharray": AOI_INACTIVE_LINE_DASH,
      },
      filter: [
        "all",
        ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]],
        ["==", ["get", "active"], false],
      ],
    });

    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_draw-preview`,
      type: "line",
      source: AOI_SOURCE_ID,
      paint: { "line-color": AOI_DRAWING_FEATURE_STROKE_COLOR, "line-width": AOI_DRAWING_FEATURE_STROKE_WIDTH, "line-dasharray": [4, 3] },
      filter: ["all", ["in", ["geometry-type"], ["literal", ["LineString", "Polygon"]]], ["==", ["get", "preview"], true]],
    });

    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_draw-point`,
      type: "circle",
      source: AOI_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": ["coalesce", ["get", "color"], AOI_DRAWING_FEATURE_COLOR],
        "circle-stroke-width": AOI_DRAWING_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": AOI_DRAWING_FEATURE_STROKE_COLOR,
      },
      // Confirmed Point AOI features (properties.featureId set by drawGJ) render as a
      // maplibregl.Marker pin instead — this circle stays reserved for the transient
      // mouse-position dot shown while drawing a Polygon/Rectangle.
      filter: ["all", ["==", ["geometry-type"], "Point"], ["!", ["has", "featureId"]]],
    });

    addLayerIfMissing({
      id: `${AOI_LAYER_ID}_edit-vertex`,
      type: "circle",
      source: EDIT_AOI_SOURCE_ID,
      paint: {
        "circle-radius": 5,
        "circle-color": AOI_MODIFY_FEATURE_COLOR,
        "circle-stroke-width": AOI_MODIFY_FEATURE_STROKE_WIDTH,
        "circle-stroke-color": AOI_MODIFY_FEATURE_STROKE_COLOR,
      },
    });
  } catch
  {
    /* ignore */
  }
};

// ── Live preview while drawing ────────────────────────────────────────────────

const pushDrawLive = (S: MapDrawAOIState) =>
{
  const { map: m, livePoints: pts, mousePos: cur, mode } = S;
  if (!m) return;

  const features: object[] = buildExistingFeats(S.features, S.activeFeatureId);
  const preview = cur && pts.length > 0 ? [...pts, cur] : pts;

  if (preview.length >= 2)
  {
    const isPolygon = mode === "POLYGON" && preview.length > 2;
    const ring = isPolygon ? ([...preview, preview[0]] as [number, number][]) : null;
    const invalid = ring ? syncLiveArea(S, ring) : false;
    if (!ring) removeLiveArea(S);

    features.push({
      type: "Feature",
      geometry: {
        type: isPolygon ? "Polygon" : "LineString",
        coordinates: ring ? [ring] : preview,
      },
      properties: { preview: true, invalid },
    });
    if (cur)
    {
      features.push({ type: "Feature", geometry: { type: "Point", coordinates: cur } });
    }
  }

  m.getSource(AOI_SOURCE_ID)?.setData({ type: "FeatureCollection", features });
};

// ── Sync draw + edit sources ──────────────────────────────────────────────────

const pushDraw = (S: MapDrawAOIState, features: DrawAOIFeature[]) =>
{
  const m = S.map;
  if (!m) return;
  S.features = features ?? [];

  m.getSource(AOI_SOURCE_ID)?.setData(drawGJ(S, features));
  m.getSource(EDIT_AOI_SOURCE_ID)?.setData(editGJ(S.editingFeatureId, features));
};

// ── GeoJSON builders ──────────────────────────────────────────────────────────

const rectRing = (a: [number, number], b: [number, number]): [number, number][] => [
  [a[0], a[1]],
  [b[0], a[1]],
  [b[0], b[1]],
  [a[0], b[1]],
  [a[0], a[1]],
];

const drawGJ = (S: MapDrawAOIState, features: DrawAOIFeature[]) =>
{
  const pts = S.drawPts ?? [];
  const feats: object[] = features.map((f) =>
  {
    let geometry = f.geometry;
    if (geometry.type === "Polygon")
    {
      const coords = geometry.coordinates[0];
      geometry = { type: "Polygon", coordinates: [closePolygon(coords)] };
    }

    return {
      type: "Feature",
      id: f.id,
      properties: {
        color: (f as any).fill || AOI_FEATURE_COLOR,
        ...(f?.properties || {}),
        featureId: f.id,
        // Last writer to the source wins, and this builder runs after the plugin's
        // own setData on a basemap switch — without the stamp here every polygon
        // would spring back to a solid outline.
        active: f.id === S.activeFeatureId,
      },
      geometry,
    };
  });

  if (pts.length >= 2)
  {
    feats.push({
      type: "Feature",
      properties: { preview: true, color: AOI_DRAWING_FEATURE_COLOR },
      geometry: {
        type: "Polygon",
        coordinates: [[...pts, pts[0]]],
      },
    });
  }

  return { type: "FeatureCollection", features: feats };
};

const editGJ = (featureId: string | null, features: DrawAOIFeature[]) =>
{
  const feat = features.find((f) => f.id === featureId);
  if (!feat || feat.geometry.type !== "Polygon") return { type: "FeatureCollection", features: [] };

  let verts: AOICoordinate[] = feat.geometry.coordinates[0];

  verts = openPolygon(verts);

  return {
    type: "FeatureCollection",
    features: verts.map((c, i) => ({
      type: "Feature",
      properties: { vertexIdx: i, featId: featureId },
      geometry: { type: "Point", coordinates: c },
    })),
  };
};

// ── State management ──────────────────────────────────────────────────────────

function resetTool(S: MapDrawAOIState)
{
  clearState(S);
  S.features = [];
}

function removeTool(S: MapDrawAOIState)
{
  if (S.map) detachToolHandlers(S.map, S);
  resetTool(S);
}

function detachToolHandlers(m: MLMap, S: MapDrawAOIState)
{
  if (S.clickH) m.off("click", S.clickH);
  if (S.dblH) m.off("dblclick", S.dblH);
  if (S.mdH) m.off("mousedown", S.mdH);
  if (S.mmH) m.off("mousemove", S.mmH);
  if (S.muH) m.off("mouseup", S.muH);
  S.clickH = S.dblH = null;
  S.mdH = S.mmH = S.muH = null;
  try
  {
    m.dragPan.enable();
  } catch
  {
    /* */
  }
}

type PolygonGeometry = Extract<DrawAOIGeometry, { type: "Polygon" }>;

function patchGeom(g: PolygonGeometry, idx: number, c: AOICoordinate): PolygonGeometry
{
  return { type: "Polygon", coordinates: [g.coordinates[0].map((x, i) => (i === idx ? c : x))] };
}

// ── Point features render as real map pins, not the small draw-point circle ──

/**
 * Rebuilds the maplibregl.Marker pins for every visible Point AOI feature.
 * Called from AOIPlugin on every `features` change; `markers` is a ref-backed
 * map the caller owns so pins persist (and get cleaned up) across renders.
 */
export function syncPointMarkers(
  map: MLMap | null | undefined,
  features: DrawAOIFeature[],
  markers: Map<string, maplibregl.Marker>,
)
{
  markers.forEach((marker) => marker.remove());
  markers.clear();

  if (!map) return;

  features.forEach((f) =>
  {
    if (f.geometry.type !== "Point" || f.visible === false) return;

    const marker = new maplibregl.Marker({ color: f.fill })
      .setLngLat(f.geometry.coordinates)
      .addTo(map as unknown as maplibregl.Map);

    markers.set(f.id, marker);
  });
}
